import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { CaApiClient, ShareRole } from '../../shared/types'

export interface ShareConversationDialogProps {
  open: boolean
  conversationId?: string
  client: CaApiClient
  onClose: () => void
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export default function ShareConversationDialog(props: ShareConversationDialogProps) {
  const intl = useIntl()
  const ROLE_OPTIONS: { value: ShareRole; label: string }[] = [
    { value: 'viewer', label: intl.formatMessage({ id: 'caChat.shareDialog.role.viewer' }) },
    { value: 'editor', label: intl.formatMessage({ id: 'caChat.shareDialog.role.editor' }) },
  ]
  const { open, conversationId, client, onClose } = props
  const qc = useQueryClient()
  const [uuid, setUuid] = useState('')
  const [role, setRole] = useState<ShareRole>('viewer')
  const [error, setError] = useState<string | undefined>()

  const shares = useQuery(
    ['ca', 'shares', conversationId],
    () => client.listShares(conversationId as string),
    { enabled: open && !!conversationId },
  )

  const invalidate = () => qc.invalidateQueries(['ca', 'shares', conversationId])

  const share = useMutation(
    (args: { target: string; role: ShareRole }) =>
      client.shareConversation(conversationId as string, args.target, args.role),
    {
      onSuccess: () => { setUuid(''); setError(undefined); invalidate() },
      onError: (e: unknown) => setError(cleanError(e)),
    },
  )

  const revoke = useMutation(
    (target: string) => client.unshareConversation(conversationId as string, target),
    { onSuccess: invalidate, onError: (e: unknown) => setError(cleanError(e)) },
  )

  const submit = () => {
    const value = uuid.trim()
    if (!UUID_RE.test(value)) {
      setError(intl.formatMessage({ id: 'caChat.shareDialog.error.invalidUuid' }))
      return
    }
    setError(undefined)
    share.mutate({ target: value, role })
  }

  const people = shares.data ?? []

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>{intl.formatMessage({ id: 'caChat.shareDialog.title' })}</Typography>
        <IconButton aria-label={intl.formatMessage({ id: 'caChat.shareDialog.aria.close' })} size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
          <TextField
            label={intl.formatMessage({ id: 'caChat.shareDialog.field.uuid' })}
            fullWidth={true}
            size="small"
            value={uuid}
            error={!!error}
            onChange={(e) => setUuid(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { submit() } }}
          />
          <TextField
            select={true}
            label={intl.formatMessage({ id: 'caChat.shareDialog.field.role' })}
            size="small"
            value={role}
            onChange={(e) => setRole(e.target.value as ShareRole)}
            sx={{ minWidth: 120 }}
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': intl.formatMessage({ id: 'caChat.shareDialog.aria.roleNew' }) }}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </TextField>
          <Button variant="contained" onClick={submit} disabled={share.isLoading} sx={{ mt: 0.25 }}>
            {intl.formatMessage({ id: 'caChat.shareDialog.button.share' })}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          <FormattedMessage
            id="caChat.shareDialog.caption"
            values={{ b: (chunks: React.ReactNode) => <b>{chunks}</b> }}
          />
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ mt: 2 }}>{intl.formatMessage({ id: 'caChat.shareDialog.section.peopleWithAccess' })}</Typography>
        {people.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: 'caChat.shareDialog.empty.notShared' })}
          </Typography>
        ) : (
          <List dense={true}>
            {people.map((p) => (
              <ListItem
                key={p.targetUserId}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      select={true}
                      size="small"
                      variant="standard"
                      value={p.role ?? 'viewer'}
                      onChange={(e) =>
                        share.mutate({ target: p.targetUserId, role: e.target.value as ShareRole })}
                      SelectProps={{ native: true }}
                      inputProps={{ 'aria-label': intl.formatMessage({ id: 'caChat.shareDialog.aria.roleFor' }, { userId: p.targetUserId }) }}
                      sx={{ '& .MuiInput-input': { fontSize: 13 } }}
                    >
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </TextField>
                    <Tooltip title={intl.formatMessage({ id: 'caChat.shareDialog.tooltip.revoke' })}>
                      <IconButton
                        aria-label={intl.formatMessage({ id: 'caChat.shareDialog.aria.revoke' })}
                        edge="end"
                        size="small"
                        onClick={() => revoke.mutate(p.targetUserId)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={p.targetUserId}
                  primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 320 } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}

function cleanError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  return msg.replace(/^CA API \d+:\s*/, '')
}
